import { ICommonSettings, IGoogleMcpMapSetting } from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import * as React from "react";
import { IWithSDProps } from "../../../../../shared/hoc/screen-design-consumer";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

export interface IConfigGoogleMapMcpSettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingConfig?: IGoogleMcpMapSetting;
}

export interface IConfigGoogleMapMcpSettingsProps {
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingGoogleMcpMapId?: string;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => React.ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigGoogleMapMcpSettings extends React.PureComponent<
  IConfigGoogleMapMcpSettingsProps,
  IConfigGoogleMapMcpSettingsState
> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigGoogleMapMcpSettingsProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle"
    };
  }

  componentDidMount() {
    this.loadSetting();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigGoogleMapMcpSettingsProps & IWithSDProps) {
    if (prevProps.editingGoogleMcpMapId !== this.props.editingGoogleMcpMapId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    }
  }

  loadSetting = () => {
    if (!this.props.editingGoogleMcpMapId) return;

    const editingConfig = this.props.commonSettings.googleMapMcpSettings?.find(
      (x) => x.id === this.props.editingGoogleMcpMapId
    );

    if (!editingConfig) return;
    this.setState({ editingConfig }, () => {
      this.formRef.current?.resetFields();
      this.formRef.current?.setFieldsValue(editingConfig);
    });
  };

  existingNameValidator = async (_rule, value) => {
    const googleMcpMapSetting = this.props.commonSettings?.googleMapMcpSettings;
    if (!googleMcpMapSetting) return;

    const isSettingAlreadyExist = googleMcpMapSetting.some(
      (x) => x.name === value && x.id !== this.props.editingGoogleMcpMapId
    );

    if (isSettingAlreadyExist) throw new Error("Google Mcp Map Name. is already exist.");
  };

  onFinish = (data: any) => {
    this.saveStatus.start();
    let obj: IGoogleMcpMapSetting;
    obj = {
      id: Guid.create().toString(),
      name: data.name,
      apiKey: data.apiKey
    };

    if (this.props.editingGoogleMcpMapId) {
      obj.id = this.props.editingGoogleMcpMapId;
      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "googleMapMcpSettings",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "googleMapMcpSettings",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (errorInfo: any) => {};

  deleteSetting = () => {
    if (!this.props.editingGoogleMcpMapId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingGoogleMcpMapId || "",
      configKey: "googleMapMcpSettings",

      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`Google Mcp Map setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  render() {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="Google Mcp Map setting"
        secondHeader="Google Mcp Map setting"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingGoogleMcpMapId ? this.deleteSetting : undefined}
            deleteTestId="mcpGoogleMapSettingDeleteButton"
            submitTestId="mcpGoogleMapSettingCreateButton"
            submitLabel={this.props.editingGoogleMcpMapId ? "Update" : "Create"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configGoogleMcpMapForm"
          />
        }
      >
        <Form
          id="configGoogleMcpMapForm"
          name="googleMcpMapSetting"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
          style={{ height: "100%" }}
        >
          <ConfigModalBody description={this.props.getSectionDescription("mcp_googlemcpmapsetting_description")}>
            <ConfigField label="Name" required tooltipContentKey="mcp_googlemcpmapsetting_name">
              <Form.Item
                name="name"
                rules={[
                  { required: true, message: "Please enter a Google Mcp Map Name." },
                  { pattern: /^[A-Za-z][A-Za-z0-9 ]+$/, message: "Google Mcp Map Name is invalid" },
                  { validator: this.existingNameValidator }
                ]}
              >
                <KuikaInput size="md" data-testid="mcpGoogleMapSettingNameInput" autoFocus />
              </Form.Item>
            </ConfigField>
            <ConfigField label="API Key" required tooltipContentKey="mcp_googlemcpmapsetting_apikey">
              <Form.Item name="apiKey" rules={[{ required: true, message: "Please enter a Google Mcp Map API Key." }]}>
                <KuikaInput size="md" data-testid="mcpGoogleMapSettingApiKeyInput" autoFocus />
              </Form.Item>
            </ConfigField>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
